import { TreeNode } from 'primeng/api';

/**
 * Utilities for tiered job sources ("How did they hear about us?").
 *
 * A source is stored as a single string. Tiers are joined with
 * `SOURCE_TIER_SEPARATOR`, e.g. `'Realtor > Remax > Betty White'`. A legacy flat
 * value like `'Radio'` is a valid depth-1 path. See `docs/tiered-job-sources.md`.
 *
 * NOTE: The separator + split/tier1Of/isInSourceTier logic is duplicated on the
 * backend in `backend/src/services/jobs/source-tiers.helpers.ts` (frontend and
 * backend cannot share a module today). Keep the two in sync.
 */

export const SOURCE_TIER_SEPARATOR = ' > ';

/**
 * 'Realtor > Remax' -> ['Realtor', 'Remax']; trims segments, drops empties.
 * Splits on `>` with any surrounding whitespace so that values entered with
 * inconsistent spacing ('Realtor>Remax', 'Realtor  >  Remax') normalize the same.
 */
export function splitSourcePath(source: string): string[] {
  if (!source) { return []; }
  return source
    .split(/\s*>\s*/)
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);
}

export function joinSourcePath(tiers: string[]): string {
  return tiers
    .map((tier) => tier.trim())
    .filter((tier) => tier.length > 0)
    .join(SOURCE_TIER_SEPARATOR);
}

/**
 * First tier: tier1Of('Realtor > Remax > Betty White') === 'Realtor'.
 * Returns the (trimmed) input unchanged for flat legacy values.
 */
export function tier1Of(source: string): string {
  const [first] = splitSourcePath(source);
  return first ?? (source ?? '').trim();
}

/**
 * True if `source` equals `tierPath` or is a descendant of it. Compares on
 * trimmed/normalized paths so `'Realtor>Remax'` and `'Realtor > Remax'` match.
 */
export function isInSourceTier(source: string, tierPath: string): boolean {
  const normalizedSource = joinSourcePath(splitSourcePath(source));
  const normalizedTier = joinSourcePath(splitSourcePath(tierPath));
  if (!normalizedTier) { return false; }
  return (
    normalizedSource === normalizedTier
    || normalizedSource.startsWith(normalizedTier + SOURCE_TIER_SEPARATOR)
  );
}

/**
 * Given a full source and an ancestor tier path, return the path one tier
 * deeper than the ancestor (or the source itself if it IS the ancestor, i.e. no
 * sub-source).
 * E.g. childTierOf('Realtor > Remax > Betty White', 'Realtor') === 'Realtor > Remax'.
 */
export function childTierOf(source: string, ancestorPath: string): string {
  const sourceTiers = splitSourcePath(source);
  const ancestorTiers = splitSourcePath(ancestorPath);
  // One tier deeper than the ancestor, but never beyond the source itself.
  const depth = Math.min(ancestorTiers.length + 1, sourceTiers.length);
  return joinSourcePath(sourceTiers.slice(0, depth));
}

/** Last segment, for display labels: lastTierOf('Realtor > Remax') === 'Remax'. */
export function lastTierOf(source: string): string {
  const tiers = splitSourcePath(source);
  return tiers.length ? tiers[tiers.length - 1] : (source ?? '').trim();
}

/**
 * Build PrimeNG TreeNode[] from flat path strings. Every node (including
 * non-leaves) is selectable; node.key/node.data = full path up to that node;
 * node.label = last segment. Shared prefixes are merged. Config order is
 * preserved (no alphabetical sort).
 */
export function buildSourceTree(paths: string[]): TreeNode[] {
  const roots: TreeNode[] = [];
  // Index nodes by their full path so shared prefixes reuse the same node.
  const byPath = new Map<string, TreeNode>();

  for (const rawPath of paths || []) {
    const tiers = splitSourcePath(rawPath);
    if (!tiers.length) { continue; }

    let parentChildren = roots;
    let accumulated = '';

    for (const tier of tiers) {
      accumulated = accumulated ? joinSourcePath([accumulated, tier]) : tier;

      let node = byPath.get(accumulated);
      if (!node) {
        node = {
          key: accumulated,
          label: tier,
          data: accumulated,
          children: [],
        };
        byPath.set(accumulated, node);
        parentChildren.push(node);
      }

      parentChildren = node.children;
    }
  }

  // Drop empty children arrays so leaves render without an expand chevron.
  for (const node of byPath.values()) {
    if (node.children && !node.children.length) {
      delete node.children;
    }
  }

  return roots;
}
